import React, { useCallback, useState } from "react";

const initialState: {
  highlightedParagraphIds: string[];
  getHighlightedIds: () => string[];
  getHighlightedCount: () => number;
  isHighlighted: (id: string) => boolean;
  setHighlightedIds: (ids: string[]) => void;
  addHighlightedId: (id: string) => void;
  addHighlightedIds: (ids: string[]) => void;
  removeHighlightedId: (id: string) => void;
  removeHighlightedIds: (ids: string[]) => void;
  clearHighlightedIds: () => void;
  toggleHighlightedId: (id: string) => void;
} = {
  highlightedParagraphIds: [],
  getHighlightedIds: () => [],
  getHighlightedCount: () => 0,
  isHighlighted: (id: string) => false,
  setHighlightedIds: (ids: string[]) => {},
  addHighlightedId: (id: string) => {},
  addHighlightedIds: (ids: string[]) => {},
  removeHighlightedId: (id: string) => {},
  removeHighlightedIds: (ids: string[]) => {},
  clearHighlightedIds: () => {},
  toggleHighlightedId: (id: string) => {},
};
const HighlightedParagraphsContext = React.createContext(initialState);

type HighlightedParagraphsProviderProps = {
  children: React.ReactNode;
};

function HighlightedParagraphsProvider({
  children,
}: HighlightedParagraphsProviderProps) {
  const [highlightedParagraphIds, setHighlightedParagraphIds] = useState<
    string[]
  >([]);

  // Get all highlighted paragraph IDs
  const getHighlightedIds = useCallback(() => {
    return highlightedParagraphIds;
  }, [highlightedParagraphIds]);

  // Set highlighted paragraph IDs (replaces existing)
  const setHighlightedIds = useCallback((ids: string[]) => {
    if (Array.isArray(ids)) {
      setHighlightedParagraphIds(ids);
    } else {
      console.warn("setHighlightedIds expects an array");
    }
  }, []);

  // Add a single paragraph ID to highlights
  const addHighlightedId = useCallback((id: string) => {
    if (typeof id === "string") {
      setHighlightedParagraphIds((prev) =>
        prev.includes(id) ? prev : [...prev, id]
      );
    } else {
      console.warn("addHighlightedId expects a string ID");
    }
  }, []);

  // Add multiple paragraph IDs to highlights
  const addHighlightedIds = useCallback((ids: string[]) => {
    if (Array.isArray(ids)) {
      setHighlightedParagraphIds((prev) => {
        const newIds = ids.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    } else {
      console.warn("addHighlightedIds expects an array");
    }
  }, []);

  // Remove a single paragraph ID from highlights
  const removeHighlightedId = useCallback((id: string) => {
    setHighlightedParagraphIds((prev) =>
      prev.filter((prevId) => prevId !== id)
    );
  }, []);

  // Remove multiple paragraph IDs from highlights
  const removeHighlightedIds = useCallback((ids: string[]) => {
    if (Array.isArray(ids)) {
      setHighlightedParagraphIds((prev) =>
        prev.filter((prevId) => !ids.includes(prevId))
      );
    } else {
      console.warn("removeHighlightedIds expects an array");
    }
  }, []);

  // Clear all highlighted paragraph IDs
  const clearHighlightedIds = useCallback(() => {
    setHighlightedParagraphIds([]);
  }, []);

  // Check if a paragraph ID is highlighted
  const isHighlighted = useCallback(
    (id: string) => {
      return highlightedParagraphIds.includes(id);
    },
    [highlightedParagraphIds]
  );

  // Toggle a paragraph ID (add if not present, remove if present)
  const toggleHighlightedId = useCallback((id: string) => {
    setHighlightedParagraphIds((prev) =>
      prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]
    );
  }, []);

  // Get count of highlighted paragraphs
  const getHighlightedCount = useCallback(() => {
    return highlightedParagraphIds.length;
  }, [highlightedParagraphIds]);

  // return {
  //   // State
  //   highlightedParagraphIds,

  //   // Getters
  //   getHighlightedIds,
  //   getHighlightedCount,
  //   isHighlighted,

  //   // Setters
  //   setHighlightedIds,
  //   addHighlightedId,
  //   addHighlightedIds,
  //   removeHighlightedId,
  //   removeHighlightedIds,
  //   clearHighlightedIds,
  //   toggleHighlightedId,
  // };

  return (
    <HighlightedParagraphsContext.Provider
      value={{
        highlightedParagraphIds,
        getHighlightedIds,
        getHighlightedCount,
        isHighlighted,
        setHighlightedIds,
        addHighlightedId,
        addHighlightedIds,
        removeHighlightedId,
        removeHighlightedIds,
        clearHighlightedIds,
        toggleHighlightedId,
      }}
    >
      {children}
    </HighlightedParagraphsContext.Provider>
  );
}

export { HighlightedParagraphsContext, HighlightedParagraphsProvider };
